import { useState, useRef, useCallback, useEffect } from 'react';
import { t } from '@/i18n';
import {
  initialLoadings, newSapLoading, getCarrier, urgencyText, at, DEMO_START, URGENCY_CHECK,
} from '@/data/arrivalsData';
import { readAnnouncements, applyAnnouncements, clearAnnouncements } from '@/lib/announcements';

// Replace {name} placeholders in a translated string.
export const fmt = (key, vars = {}) =>
  Object.entries(vars).reduce((s, [k, v]) => s.replaceAll(`{${k}}`, v), t(key));

const clone = (obj) => JSON.parse(JSON.stringify(obj));

// Urgency demo: at 13:00 the system finds planned loadings without any
// information, messages the right carrier, reads the replies and escalates
// (second channel → voice call to the dispatcher). Real delays are short,
// the demo clock shows the simulated time.
export function useUrgencySimulation() {
  const [loadings, setLoadings] = useState(() => clone(initialLoadings));
  const [log, setLog] = useState([]);
  const [phase, setPhase] = useState('idle'); // idle | review | sending | done
  const [clock, setClock] = useState(DEMO_START);
  const [autoMode, setAutoMode] = useState(false);
  const [queue, setQueue] = useState([]);
  const timers = useRef([]);

  const later = (ms, fn) => { timers.current.push(setTimeout(fn, ms)); };
  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  // Carrier announcements: applied on load and whenever the carrier page
  // (same browser, any tab) saves one.
  const syncAnnouncements = useCallback(() => {
    setLoadings(prev => applyAnnouncements(prev, readAnnouncements()));
  }, []);
  useEffect(() => {
    syncAnnouncements();
    window.addEventListener('storage', syncAnnouncements);
    return () => window.removeEventListener('storage', syncAnnouncements);
  }, [syncAnnouncements]);

  const addLog = useCallback((time, entry) => {
    setClock(time);
    setLog(prev => [{ id: `${time}-${prev.length}`, time, ...entry }, ...prev]);
  }, []);

  // Patch one loading and optionally append a message to its history.
  const patch = useCallback((id, changes, message) => {
    setLoadings(prev => prev.map(l => {
      if (l.id !== id) return l;
      const next = { ...l, ...changes };
      if (message) {
        next.messages = [...l.messages, message];
        next.lastContact = message.time;
      }
      return next;
    }));
  }, []);

  const reply = (time, id, channel, text, textCs, changes, interpretation) => {
    const msg = { time: at(time), dir: 'in', channel, text, textCs };
    patch(id, changes, msg);
    addLog(time, { kind: 'in', loadingId: id, channel, text, textCs, interpretation });
  };

  const sendOne = (time, loading, channel) => {
    const { text, textCs } = urgencyText(loading);
    patch(loading.id, { urged: true }, { time: at(time), dir: 'out', channel, text, textCs, auto: true });
    addLog(time, { kind: 'out', loadingId: loading.id, channel, text, textCs });
  };

  const channelFor = (loading) => {
    const ch = getCarrier(loading.carrierId).channel;
    return ch === 'phone' ? 'sms' : ch;
  };

  const sendAll = useCallback((targets) => {
    setPhase('sending');
    targets.forEach(l => sendOne('13:01', l, channelFor(l)));

    later(1500, () => reply('13:04', 'K-04', 'sms', 'TAK, będzie ok. 15:30', null,
      { state: 'confirmed', eta: at('15:30') }, fmt('arrivals.ai.confirmed', { time: '15:30' })));

    later(3000, () => reply('13:07', 'K-10', 'sms', 'Dorazíme v 16:20, řidič měl zdržení v Šumperku.', null,
      { state: 'confirmed', eta: at('16:20') }, fmt('arrivals.ai.confirmed', { time: '16:20' })));

    later(4500, () => reply('13:11', 'K-02', 'link', 'Brak godzin pracy – mogę ładować od 05:30.',
      'Nemám pracovní dobu – nakládat můžu od 05:30.',
      { state: 'no-power', power: '05:30', eta: at('06:00', 1), parking: true },
      t('arrivals.ai.noPower')));

    later(6000, () => reply('13:15', 'K-08', 'email', 'Potwierdzamy przyjazd ok. 14:45.', 'Potvrzujeme příjezd kolem 14:45.',
      { state: 'confirmed', eta: at('14:45') }, fmt('arrivals.ai.confirmed', { time: '14:45' })));

    later(7500, () => reply('13:18', 'K-09', 'whatsapp', 'Dziś nie damy rady, auto będzie jutro o 6:00.',
      'Dnes to nestihneme, auto bude zítra v 6:00.',
      { state: 'rescheduled', eta: at('06:00', 1) }, fmt('arrivals.ai.rescheduled', { time: '06:00' })));

    // K-07: a new SAP order arrives, gets assigned and messaged automatically.
    later(9000, () => {
      setLoadings(prev => [...prev, clone(newSapLoading)]);
      addLog('13:20', { kind: 'sap', loadingId: 'K-07', text: fmt('arrivals.log.sapNew', { order: newSapLoading.orderId, carrier: getCarrier('D3').name }) });
      sendOne('13:20', newSapLoading, 'email');
    });
    later(10500, () => reply('13:24', 'K-07', 'email', 'Potwierdzamy, będziemy ok. 16:20.', 'Potvrzujeme, budeme kolem 16:20.',
      { state: 'confirmed', eta: at('16:20') }, fmt('arrivals.ai.confirmed', { time: '16:20' })));

    // Escalation after 30 minutes without an answer.
    later(12000, () => {
      addLog('13:31', { kind: 'escalation', text: fmt('arrivals.log.escalate', { ids: 'K-01, K-05' }) });
      const k01 = targets.find(l => l.id === 'K-01');
      const k05 = targets.find(l => l.id === 'K-05');
      if (k01) sendOne('13:31', k01, 'sms');
      if (k05) sendOne('13:31', k05, 'sms');
    });

    later(14000, () => {
      const d = getCarrier('D2').dispatcher;
      const transcript = [
        { who: 'bot', text: 'Dzień dobry, tu automatyczny asystent OMYA Pomezí. Czekamy dziś na SK 7310M, zlecenie DOD 80452311. Czy auto dziś przyjedzie?', textCs: 'Dobrý den, tady automatický asistent OMYA Pomezí. Dnes čekáme SK 7310M, zakázka DOD 80452311. Přijede auto dnes?' },
        { who: 'human', text: 'Dzisiaj nie, kierowca jest jeszcze pod Katowicami. Będzie jutro o siódmej rano.', textCs: 'Dnes ne, řidič je ještě u Katovic. Bude zítra v sedm ráno.' },
        { who: 'bot', text: 'Rozumiem, zapisuję: jutro 7:00. Dziękuję.', textCs: 'Rozumím, zapisuji: zítra 7:00. Děkuji.' },
      ];
      patch('K-01', { state: 'rescheduled', eta: at('07:00', 1) },
        { time: at('13:45'), dir: 'in', channel: 'voice', text: transcript[1].text, textCs: 'Dnes ne, řidič je ještě u Katovic. Bude zítra v sedm ráno.' });
      addLog('13:45', {
        kind: 'call', loadingId: 'K-01', channel: 'voice',
        text: fmt('arrivals.log.call', { name: d.name, phone: d.phone }),
        transcript, interpretation: fmt('arrivals.ai.rescheduled', { time: '07:00' }),
      });
    });

    later(16500, () => {
      const d = getCarrier('D5').dispatcher;
      patch('K-05', { escalated: true },
        { time: at('13:46'), dir: 'system', channel: 'voice', text: t('arrivals.log.callNoAnswerShort') });
      addLog('13:46', { kind: 'call', loadingId: 'K-05', channel: 'voice', text: fmt('arrivals.log.callNoAnswer', { name: d.name }), failed: true });
    });

    later(17500, () => {
      addLog('13:47', { kind: 'done', text: t('arrivals.log.done') });
      setPhase('done');
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addLog, patch]);

  const runCheck = useCallback(() => {
    // K-03 is found via GPS — no message needed.
    patch('K-03', { state: 'en-route', gpsKm: 48, eta: at('14:20') },
      { time: at(URGENCY_CHECK), dir: 'system', channel: 'gps', text: fmt('arrivals.log.gpsShort', { km: 48, time: '14:20' }) });
    addLog(URGENCY_CHECK, { kind: 'gps', loadingId: 'K-03', text: fmt('arrivals.log.gps', { km: 48, time: '14:20' }) });

    const targets = loadings.filter(l => l.state === 'no-info' && l.id !== 'K-03');
    setQueue(targets.map(l => l.id));
    addLog(URGENCY_CHECK, {
      kind: 'system',
      text: fmt('arrivals.log.check', { total: targets.length + 1, gps: 1, queue: targets.length }),
    });

    if (autoMode) later(1200, () => sendAll(targets));
    else setPhase('review');
  }, [loadings, autoMode, patch, addLog, sendAll]);

  const approve = useCallback(() => {
    sendAll(loadings.filter(l => queue.includes(l.id)));
  }, [loadings, queue, sendAll]);

  // Adds validated rows from the plan import (src/lib/planImport.js) as loadings without information.
  const importLoadings = useCallback((rows) => {
    setLoadings(prev => [...prev, ...rows.map(r => ({
      id: `I-${r.orderId.replace(/\D/g, '').slice(-5)}`,
      source: 'import',
      carrierId: r.carrier.id,
      plate: '—',
      driver: { name: '—', phone: null },
      orderId: r.orderId,
      customer: r.customer || '—',
      material: r.material || '—',
      type: r.type,
      weight: r.weight,
      gate: r.type === 'silo' ? 'CLS' : 'PST',
      plannedAt: r.plannedAt.toISOString(),
      eta: null,
      state: 'no-info',
      power: null,
      gpsKm: null,
      lastContact: null,
      messages: [],
    }))]);
  }, []);

  // A carrier reply read by the AI (#65): the reply joins the history, state and time change.
  // `eta` is an ISO time, `power` the "HH:MM" from which the driver may drive again.
  const applyReply = useCallback((id, { text, textCs, channel, state, eta, power }) => {
    const changes = { state, ...(eta ? { eta } : {}), ...(power ? { power } : {}) };
    patch(id, changes, { time: new Date().toISOString(), dir: 'in', channel, text, textCs, ai: true });
  }, [patch]);

  // An accepted AI re-planning proposal (#68): new time, noted in the history.
  const applyPlan = useCallback((id, { eta, summary }) => {
    patch(id, { state: 'rescheduled', eta }, { time: new Date().toISOString(), dir: 'system', channel: 'system', text: summary, ai: true });
  }, [patch]);

  // A document read by the AI (#70) is noted in the loading's history.
  const attachDocument = useCallback((id, summary) => {
    patch(id, {}, { time: new Date().toISOString(), dir: 'system', channel: 'system', text: summary, ai: true });
  }, [patch]);

  // Work queue actions (#104): follow up one loading now, or close it by hand.
  const urgeOne = useCallback((id) => {
    const loading = loadings.find(l => l.id === id);
    if (loading) sendOne(clock, loading, channelFor(loading));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadings, clock]);

  const resolve = useCallback((id, note) => {
    patch(id, { resolved: true }, { time: new Date().toISOString(), dir: 'system', channel: 'system', text: note });
  }, [patch]);

  const reset = useCallback(() => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
    clearAnnouncements();
    setLoadings(clone(initialLoadings));
    setLog([]);
    setQueue([]);
    setPhase('idle');
    setClock(DEMO_START);
  }, []);

  return { loadings, log, phase, clock, queue, autoMode, setAutoMode, runCheck, approve, reset, importLoadings, syncAnnouncements, applyReply, applyPlan, attachDocument, urgeOne, resolve };
}
